import { Grid, Typography, Switch } from '@material-ui/core';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Hidden } from '@mui/material';
import { Dispatch, VFC, useContext, useState, useEffect } from 'react';
import { useForm, UseFormReturn, FormProvider } from 'react-hook-form';
import { useHooks } from './useHooks';
import { useStyles } from './useStyles';
import { SiteProcessTextfield } from '../../input/ProcessTextfield';
import { SiteProcessSelectfield } from '../../input/ProcessSelectfield';
import { SiteProcessDatePicker } from '../../input/ProcessDatePicker';
import { validationMessage } from '../../../../const/ProcessConst';
import { BaseModal } from '../../../molecules/BaseModal';
import { ModalSubmitButton } from '../../cloudStorage/style/style';
import { TAddDailyReportForm } from '../../../../types/dailyReport';
import { DailyReportImageListItem } from '../DailyReportImageListItem';
import { CustomTooltip } from '../../../tooltip/CustomTooltip';
import { tooltipContents } from '../../../tooltip/Const';
import HelpIcon from '@material-ui/icons/Help';
import {
  UNSIGNED_INT_VALIDATION,
  UNSIGNED_INT_VALIDATION_MSG,
  MAX_IMAGE_COUNT
} from '../../../../const/DailyReportConst';
import LoadingIconClear from '../../../atoms/LoadingIconClear';
import { AuthInfoContext } from '../../../../AuthContextProvider';
import { ROLE } from '../../../../const/Const';
import { acceptablePictureTypeStr } from '../../../../utils/common';

export type Props = {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
  afterSubmit: () => void;
  constructId?: number;
};

export const DailyReportAddModal: VFC<Props> = props => {
  const { isOpen, setIsOpen, afterSubmit, constructId } = props;
  const classes = useStyles();
  const [authInfo, setAuthInfo] = useContext(AuthInfoContext);

  const [photoSyncEnabled, setPhotoSyncEnabled] = useState(false);

  const methods: UseFormReturn<TAddDailyReportForm> = useForm({
    reValidateMode: 'onSubmit'
  });
  const {
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  const {
    cancelSubmit,
    submit,
    constructList,
    files,
    deletePicture,
    getRootProps,
    getInputProps,
    handleCaptureImage,
    isSending
  } = useHooks({
    setIsOpen,
    afterSubmit,
    getValues,
    setValue,
    reset,
    isOpen,
    constructIdDef: constructId,
    photoSyncEnabled
  });

  const theme = useTheme();
  const isSmDownScreen = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    const savePhotoSyncEnabled = localStorage.getItem('photoSyncEnabled');
    if (savePhotoSyncEnabled !== null) {
      setPhotoSyncEnabled(JSON.parse(savePhotoSyncEnabled));
    }
  }, []);

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      handleSubmit={handleSubmit(submit, (erros: any) => {})}
      handleCancelSubmit={cancelSubmit}
      title={'日報作成'}
      buttonCaption={'追加する'}
      cancelButtonCaption={'キャンセル'}
    >
      {isSending && (
        <div className={classes.loadingIconClearWrapper}>
          <LoadingIconClear />
        </div>
      )}
      <Grid container xs={12} md={10} alignItems='center' justify='center'>
        <FormProvider {...methods}>
          {/* 日付 */}
          <Grid item xs={10} md={2} className={classes.gridItem}>
            <label className={classes.label}>日付*</label>
          </Grid>
          <Grid item xs={10} md={10}>
            <div className={classes.leftAlignContainer}>
              <Grid item xs={7} md={5}>
                <div className={classes.itemMargin}>
                  <SiteProcessDatePicker
                    name='work_day'
                    label='日付'
                    required={true}
                    fullWidth={true}
                    errorMessage={errors?.work_day?.message}
                    className={classes.gridItem}
                  />
                </div>
              </Grid>
            </div>
          </Grid>
          {/* /日付 */}
          {/* 案件 */}
          <Grid container xs={12} alignItems='center' justify='center'>
            <Grid item xs={10} md={2} className={classes.gridItem}>
              <label className={classes.label}>案件*</label>
            </Grid>
            <Grid item xs={10} md={10}>
              <SiteProcessSelectfield
                name='construct_id'
                listDatas={constructList}
                className={`${classes.input} ${classes.gridItem}`}
                required={true}
                error={'construct_id' in errors}
                errorMessage={
                  errors?.construct_id && validationMessage.required
                }
              />
            </Grid>
          </Grid>
          {/* /案件 */}
          {/* 報告 */}
          <Grid item xs={10} md={2} className={classes.gridItem}>
            <label className={classes.label}>報告*</label>
          </Grid>
          <Grid item xs={10} md={10}>
            <SiteProcessTextfield
              name='report_detail'
              required={true}
              fullWidth={true}
              multiline={true}
              style={{ padding: 0 }}
              maxLength={1000}
              className={`${classes.input} ${classes.gridItem}`}
              errorMessage={errors?.report_detail?.message}
              inputProps={{ minHeight: 60, maxHeight: 100 }}
              inputElementProps={{ style: { padding: '2px 14px' } }}
            />
          </Grid>
          {/* /報告 */}
          {/* 写真 */}
          <Grid item xs={10} md={2} className={classes.gridItem}>
            <label className={classes.label}>写真</label>
          </Grid>
          {/* レスポンシブ */}
          {!isSmDownScreen && (
            <Grid
              container
              xs={12}
              spacing={1}
              alignItems='center'
              justify='center'
            >
              <Grid
                item
                xs={5}
                className={`${classes.referFileButtonWrapperRwd}`}
              >
                <input
                  accept='image/*'
                  id='fileButton'
                  type='file'
                  style={{ display: 'none' }}
                  onChange={handleCaptureImage}
                  multiple={true}
                />
                <label
                  htmlFor='fileButton'
                  className={classes.referFileButtonRwd}
                >
                  ファイルを参照
                </label>
              </Grid>
              <Grid
                item
                xs={5}
                className={`${classes.referFileButtonWrapperRwd}`}
              >
                <input
                  accept='image/*'
                  capture='environment'
                  id='cameraButton'
                  type='file'
                  style={{ display: 'none' }}
                  onChange={handleCaptureImage}
                />
                <label
                  htmlFor='cameraButton'
                  className={classes.referFileButtonRwd}
                >
                  カメラ撮影
                </label>
              </Grid>
              <Grid item xs={10}>
                <Typography variant='body2' align='center' gutterBottom={true}>
                  ※保存可能なファイル形式：
                  <br />
                  {acceptablePictureTypeStr()}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid item xs={10} md={10} className={classes.gridItem}>
            {/* 標準レイアウト */}
            {isSmDownScreen && (
              <div className={classes.dropArea} {...getRootProps()}>
                <input {...getInputProps()} />
                <Typography variant='body1' align='center' gutterBottom={true}>
                  画像をドラッグ&ドロップしてください。
                </Typography>
                <Grid item className={classes.referFileButtonWrapper}>
                  <ModalSubmitButton variant='contained' onClick={() => {}}>
                    ファイルを参照
                  </ModalSubmitButton>
                </Grid>
                <Typography variant='body2' align='center' gutterBottom={true}>
                  または、ファイル参照より画像を選択してください。
                </Typography>
                <Typography variant='body2' align='center' gutterBottom={true}>
                  ※保存可能なファイル形式：{acceptablePictureTypeStr()}
                </Typography>
              </div>
            )}
            {files.length > 0 && (
              <>
                <div className={classes.imageListContainer}>
                  {files.map((file: File, index: number) => (
                    <DailyReportImageListItem
                      file={file}
                      index={index}
                      deletePicture={deletePicture}
                    />
                  ))}
                </div>
                <Typography variant='subtitle2' align='right'>
                  {`選択枚数 ${files.length}/${MAX_IMAGE_COUNT}`}
                </Typography>
              </>
            )}
          </Grid>
          {/* /写真 */}
          {/* ストレージ自動連携 */}
          <Grid item xs={10} md={2} className={classes.gridItem}>
            <label className={classes.label}>
              ストレージ
              <br />
              自動連携
            </label>
          </Grid>
          <Grid item xs={10} md={10} className={classes.gridItem}>
            <Grid container alignItems='center' spacing={1}>
              <Grid item>OFF</Grid>
              <Grid item>
                <Switch
                  checked={photoSyncEnabled}
                  onChange={e => {
                    const newValue = e.target.checked;
                    setPhotoSyncEnabled(newValue);
                    // ローカルストレージに状態を保存
                    localStorage.setItem(
                      'photoSyncEnabled',
                      JSON.stringify(newValue)
                    );
                  }}
                  color='primary'
                  name='photoSyncEnabled'
                  inputProps={{ 'aria-label': 'storage sync toggle' }}
                />
              </Grid>
              <Grid item>ON</Grid>
              <Grid item>
                <CustomTooltip title={tooltipContents.PhotoSyncEnabledButton}>
                  <HelpIcon color='action' />
                </CustomTooltip>
              </Grid>
            </Grid>
          </Grid>
          {/* ストレージ自動連携 */}

          {authInfo?.role != ROLE['OUTSIDE_PARTNER'] && (
            <>
              {/* 原価UP */}
              <Grid item xs={10} md={2} className={classes.gridItem}>
                <label className={classes.label}>
                  原価UP
                  <br />
                  要因
                </label>
              </Grid>
              <Grid item xs={10} md={6}>
                <SiteProcessTextfield
                  name='cost_up_factor'
                  maxLength={100}
                  className={`${classes.input} ${classes.gridItem}`}
                  errorMessage={errors?.cost_up_factor?.message}
                  multiline={true}
                  inputProps={{ height: 68 }}
                  inputElementProps={{ style: { padding: '2px 14px' } }}
                />
              </Grid>
              <Grid item xs={10} md={4} className={classes.costSubContainer}>
                <label className={`${classes.label} ${classes.gridItem}`}>
                  目安金額
                </label>
                <SiteProcessTextfield
                  name='cost_up_amount'
                  pattern={UNSIGNED_INT_VALIDATION}
                  patternMessage={UNSIGNED_INT_VALIDATION_MSG}
                  className={`${classes.input} ${classes.gridItem}`}
                  max={100000000}
                  errorMessage={errors?.cost_up_amount?.message}
                  inputProps={{
                    height: 34,
                    padding: '0 14px',
                    textAlign: 'right'
                  }}
                />
              </Grid>
              {/* /原価UP */}
              {/* 原価DOWN */}
              <Grid item xs={10} md={2} className={classes.gridItem}>
                <label className={classes.label}>
                  原価DOWN
                  <br />
                  要因
                </label>
              </Grid>
              <Grid item xs={10} md={6}>
                <SiteProcessTextfield
                  name='cost_down_factor'
                  maxLength={100}
                  className={`${classes.input} ${classes.gridItem}`}
                  errorMessage={errors?.cost_down_factor?.message}
                  multiline={true}
                  inputProps={{ height: 68 }}
                  inputElementProps={{ style: { padding: '2px 14px' } }}
                />
              </Grid>
              <Grid item xs={10} md={4} className={classes.costSubContainer}>
                <label className={`${classes.label} ${classes.gridItem}`}>
                  目安金額
                </label>
                <SiteProcessTextfield
                  name='cost_down_amount'
                  pattern={UNSIGNED_INT_VALIDATION}
                  patternMessage={UNSIGNED_INT_VALIDATION_MSG}
                  className={`${classes.input} ${classes.gridItem}`}
                  max={100000000}
                  errorMessage={errors?.cost_down_amount?.message}
                  inputProps={{
                    height: 34,
                    padding: '0 14px',
                    textAlign: 'right'
                  }}
                />
              </Grid>
              {/* /原価DOWN */}
            </>
          )}
        </FormProvider>
      </Grid>
    </BaseModal>
  );
};
